import * as THREE from 'three';
import type { SkyDome } from './SkyDome';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

/**
 * Real weather and real daylight in the yard. Asks for the phone's location
 * once, then fetches Open-Meteo (free, no key): current conditions plus
 * today's sunrise/sunset. Sky, sun, fog and ground follow; rain and snow are
 * particle fields. Without location or network it stays a clear day.
 */
export type Sky = 'clear' | 'cloudy' | 'rain' | 'snow';

interface Target {
  sky: Sky;
  sunIntensity: number;
  hemi: number;
  skyColor: THREE.Color;
  groundColor: THREE.Color;
  fogNear: number;
  fogFar: number;
  sunColor: THREE.Color;
  /** how much of the overcast sky photo shows (0 clear .. 1) */
  overcast: number;
  /** multiplies the sky photo: white by day, dark blue at night */
  tint: THREE.Color;
}

const _c = new THREE.Color();
const _c2 = new THREE.Color();
const _base = new THREE.Color();
const _nightTint = new THREE.Color();

function skyFromCode(code: number): Sky {
  if (code >= 71 && code <= 77) return 'snow';
  if (code === 85 || code === 86) return 'snow';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82) || code >= 95) return 'rain';
  if (code >= 2) return 'cloudy';
  return 'clear';
}

/**
 * A pinned daylight (1 = noon, 0 = night) instead of the real sun: `?daylight=1` for a session,
 * or the care-panel switch, which is remembered on the device (`lunaloo.daylight`).
 */
export const daylightOverride = { value: null as number | null };
{
  const v = parseFloat(new URLSearchParams(location.search).get('daylight') ?? '');
  if (Number.isFinite(v)) daylightOverride.value = Math.min(1, Math.max(0, v));
  else {
    try {
      if (localStorage.getItem('lunaloo.daylight') === 'noon') daylightOverride.value = 1;
    } catch {
      /* no storage */
    }
  }
}

export class Weather {
  sky: Sky = 'clear';
  /** 0 night .. 1 full day, from real sunrise/sunset when known */
  daylight = 1;
  temperatureC: number | null = null;
  /** party mode: coloured confetti drifting down */
  confetti = false;
  private confettiPts!: THREE.Points;
  /** short label for the status pill, e.g. "rain 12°C" or "night" */
  get label(): string {
    const parts: string[] = [];
    if (this.daylight < 0.5) parts.push('night');
    if (this.sky !== 'clear' || this.temperatureC !== null) parts.push(this.sky === 'clear' ? 'clear' : this.sky);
    if (this.temperatureC !== null) parts.push(`${Math.round(this.temperatureC)}°C`);
    return parts.join(' ');
  }
  onStatus?: (text: string) => void;
  private sunrise: number | null = null; // minutes after midnight, local
  private sunset: number | null = null;
  private rain: THREE.Points;
  private snow: THREE.Points;
  private rainVel: Float32Array;
  private snowPhase: Float32Array;
  private cur: Target;
  /** the photo sky, once main has made one: it gets the overcast mix and the tint, and lends its horizon colour to the fog */
  dome: SkyDome | null = null;
  private baseGround: THREE.Color;
  private lastFetch = 0;
  private coords: { lat: number; lon: number } | null = null;

  constructor(
    private scene: THREE.Scene,
    private sun: THREE.DirectionalLight,
    private hemi: THREE.HemisphereLight,
    private ground: THREE.MeshStandardMaterial,
  ) {
    this.baseGround = ground.color.clone();
    this.cur = this.target('clear', 1);
    const mk = (n: number, size: number, color: number, opacity: number): [THREE.Points, Float32Array] => {
      const pos = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        pos[i * 3] = (Math.random() - 0.5) * 16;
        pos[i * 3 + 1] = Math.random() * 7;
        pos[i * 3 + 2] = -10 + Math.random() * 12;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const m = new THREE.PointsMaterial({ color, size, transparent: true, opacity, depthWrite: false });
      const p = new THREE.Points(g, m);
      p.visible = false;
      p.frustumCulled = false;
      scene.add(p);
      return [p, pos];
    };
    [this.rain] = mk(900, 0.05, 0xbfd4e6, 0.55);
    {
      const n = 250;
      const pos = new Float32Array(n * 3);
      const col = new Float32Array(n * 3);
      const palette = [0x7b5cc4, 0xf2c94c, 0xff4b4b, 0x4bd36a, 0x4b8bff];
      for (let i = 0; i < n; i++) {
        pos[i * 3] = (Math.random() - 0.5) * 8;
        pos[i * 3 + 1] = Math.random() * 5;
        pos[i * 3 + 2] = -6 + Math.random() * 8;
        const c = new THREE.Color(palette[i % palette.length]);
        col[i * 3] = c.r;
        col[i * 3 + 1] = c.g;
        col[i * 3 + 2] = c.b;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      this.confettiPts = new THREE.Points(g, new THREE.PointsMaterial({ size: 0.07, vertexColors: true, transparent: true, opacity: 0.95, depthWrite: false }));
      this.confettiPts.visible = false;
      this.confettiPts.frustumCulled = false;
      scene.add(this.confettiPts);
    }
    [this.snow] = mk(600, 0.09, 0xffffff, 0.9);
    this.rainVel = new Float32Array(900);
    for (let i = 0; i < 900; i++) this.rainVel[i] = 7 + Math.random() * 3;
    this.snowPhase = new Float32Array(600);
    for (let i = 0; i < 600; i++) this.snowPhase[i] = Math.random() * Math.PI * 2;
  }

  /**
   * Find out where we are: the phone's location (asked from a user gesture),
   * retried once on a slow fix, else an approximate position from the network
   * address so the weather and the sunset are still right for the city.
   */
  start(): void {
    const useCoords = (lat: number, lon: number, approx: boolean): void => {
      this.coords = { lat, lon };
      this.approximate = approx;
      this.onLocation?.(lat, lon);
      void this.fetch();
    };
    const approxFallback = async (why: string): Promise<void> => {
      try {
        const j = (await (await fetch('https://ipapi.co/json/')).json()) as { latitude?: number; longitude?: number; city?: string };
        if (typeof j.latitude === 'number' && typeof j.longitude === 'number') {
          useCoords(j.latitude, j.longitude, true);
          this.onStatus?.(`${why} — using ${j.city ?? 'your area'} instead`);
          return;
        }
      } catch {
        /* fall through */
      }
      this.onStatus?.(`${why} — clear skies by default`);
    };
    if (Capacitor.isNativePlatform()) {
      // the store apps: the native location service (the WebView's navigator.geolocation never prompts)
      void (async () => {
        try {
          let perm = await Geolocation.checkPermissions();
          if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') perm = await Geolocation.requestPermissions({ permissions: ['coarseLocation'] });
          if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') return void approxFallback('location blocked');
          const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 20_000, maximumAge: 3600_000 });
          useCoords(pos.coords.latitude, pos.coords.longitude, false);
        } catch {
          void approxFallback("couldn't get a location fix");
        }
      })();
      return;
    }
    if (!('geolocation' in navigator)) {
      void approxFallback('no location on this device');
      return;
    }
    let tries = 0;
    const attempt = (): void => {
      tries++;
      navigator.geolocation.getCurrentPosition(
        (pos) => useCoords(pos.coords.latitude, pos.coords.longitude, false),
        (err) => {
          if (err.code === err.PERMISSION_DENIED) void approxFallback('location blocked');
          else if (tries < 2) attempt(); // slow fix: one more go with a longer wait
          else void approxFallback("couldn't get a location fix");
        },
        { maximumAge: 3600_000, timeout: tries === 1 ? 12_000 : 30_000, enableHighAccuracy: false },
      );
    };
    attempt();
  }

  /** the position came from the network address, not the phone */
  approximate = false;
  /** we know where the phone is (also fires for the approximate fallback) */
  onLocation?: (lat: number, lon: number) => void;

  private async fetch(): Promise<void> {
    if (!this.coords) return;
    this.lastFetch = performance.now();
    try {
      const { lat, lon } = this.coords;
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(3)}&longitude=${lon.toFixed(3)}&current=temperature_2m,weather_code,is_day&daily=sunrise,sunset&timezone=auto&forecast_days=1`;
      const r = await fetch(url);
      const j = (await r.json()) as { current: { temperature_2m: number; weather_code: number; is_day: number }; daily: { sunrise: string[]; sunset: string[] } };
      this.sky = skyFromCode(j.current.weather_code);
      this.temperatureC = j.current.temperature_2m;
      const mins = (iso: string): number => {
        const [, t] = iso.split('T');
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      };
      this.sunrise = mins(j.daily.sunrise[0]);
      this.sunset = mins(j.daily.sunset[0]);
      this.onStatus?.(`${this.sky}, ${Math.round(this.temperatureC)}°C`);
    } catch {
      /* keep whatever we had */
    }
  }

  private target(sky: Sky, day: number): Target {
    const t: Target = {
      sky,
      sunIntensity: 2.2,
      hemi: 0.8,
      skyColor: new THREE.Color(0xdfe7ee),
      groundColor: this.baseGround.clone(),
      fogNear: 30,
      fogFar: 110,
      sunColor: new THREE.Color(0xfff1dc),
      overcast: 0,
      tint: new THREE.Color(0xffffff),
    };
    if (sky === 'cloudy') {
      t.sunIntensity = 1.2;
      t.hemi = 1.4;
      t.overcast = 0.85;
      t.fogNear = 24;
      t.fogFar = 80;
    } else if (sky === 'rain') {
      t.sunIntensity = 0.8;
      t.hemi = 1.3;
      t.overcast = 1;
      t.tint.set(0xd8dce0);
      t.groundColor.multiplyScalar(0.88);
      t.fogNear = 12;
      t.fogFar = 45;
    } else if (sky === 'snow') {
      t.sunIntensity = 0.9;
      t.hemi = 1.4;
      t.overcast = 1;
      t.groundColor.set(0xe9edf0);
      t.fogNear = 12;
      t.fogFar = 45;
    }
    // the sky's colour at the horizon: from the photos when there are any, else the old flat colours
    const base = _base.copy(this.dome?.horizon ?? _c.set(0xdfe7ee)).lerp(this.dome?.horizonOvercast ?? _c2.set(0xd3d9de), t.overcast);
    // night: everything dims and cools; a little moonlight keeps her visible. The tint takes the
    // photo down to the night colour at the horizon, and the fog is that same colour.
    const night = 1 - day;
    t.sunIntensity = t.sunIntensity * day + 0.25 * night;
    t.hemi = t.hemi * day + 0.25 * night;
    _nightTint.set(0x131a2a);
    _nightTint.setRGB(Math.min(1.5, _nightTint.r / Math.max(0.02, base.r)), Math.min(1.5, _nightTint.g / Math.max(0.02, base.g)), Math.min(1.5, _nightTint.b / Math.max(0.02, base.b)));
    t.tint.lerp(_nightTint, night);
    t.skyColor.copy(base).multiply(t.tint);
    t.groundColor.lerp(_c.set(0x1c2418), night * 0.85);
    t.sunColor.lerp(_c.set(0x9fb4d8), night);
    t.fogNear = t.fogNear * day + 8 * night;
    t.fogFar = t.fogFar * day + 40 * night;
    return t;
  }

  private computeDaylight(): number {
    if (this.sunrise === null || this.sunset === null) return 1;
    const d = new Date();
    const m = d.getHours() * 60 + d.getMinutes();
    const w = 40; // minutes of dawn / dusk blend
    if (m < this.sunrise - w || m > this.sunset + w) return 0;
    if (m < this.sunrise + w) return (m - (this.sunrise - w)) / (2 * w);
    if (m > this.sunset - w) return ((this.sunset + w) - m) / (2 * w);
    return 1;
  }

  update(dt: number, around: THREE.Vector3): void {
    if (this.coords && performance.now() - this.lastFetch > 30 * 60_000) void this.fetch();
    this.daylight = daylightOverride.value ?? this.computeDaylight();
    const want = this.target(this.sky, this.daylight);
    const k = 1 - Math.exp(-0.6 * dt);
    const c = this.cur;
    c.sunIntensity += (want.sunIntensity - c.sunIntensity) * k;
    c.hemi += (want.hemi - c.hemi) * k;
    c.skyColor.lerp(want.skyColor, k);
    c.groundColor.lerp(want.groundColor, k);
    c.sunColor.lerp(want.sunColor, k);
    c.overcast += (want.overcast - c.overcast) * k;
    c.tint.lerp(want.tint, k);
    this.dome?.set(c.overcast, c.tint);
    c.fogNear += (want.fogNear - c.fogNear) * k;
    c.fogFar += (want.fogFar - c.fogFar) * k;
    this.sun.intensity = c.sunIntensity;
    this.sun.color.copy(c.sunColor);
    this.hemi.intensity = c.hemi;
    (this.scene.background as THREE.Color).copy(c.skyColor);
    const fog = this.scene.fog as THREE.Fog;
    fog.color.copy(c.skyColor);
    fog.near = c.fogNear;
    fog.far = c.fogFar;
    this.ground.color.copy(c.groundColor);

    this.confettiPts.visible = this.confetti;
    if (this.confetti) {
      const a = this.confettiPts.geometry.getAttribute('position') as THREE.BufferAttribute;
      const arr = a.array as Float32Array;
      const t = performance.now() / 1000;
      for (let i = 0; i < arr.length / 3; i++) {
        arr[i * 3 + 1] -= 0.5 * dt;
        arr[i * 3] += Math.sin(t * 2 + i) * 0.4 * dt;
        if (arr[i * 3 + 1] < 0.02) {
          arr[i * 3 + 1] = 5;
          arr[i * 3] = around.x + (Math.random() - 0.5) * 8;
          arr[i * 3 + 2] = around.z - 6 + Math.random() * 8;
        }
      }
      a.needsUpdate = true;
    }

    // particles follow the viewer's neighbourhood
    this.rain.visible = this.sky === 'rain';
    this.snow.visible = this.sky === 'snow';
    if (this.rain.visible) {
      const a = this.rain.geometry.getAttribute('position') as THREE.BufferAttribute;
      const arr = a.array as Float32Array;
      for (let i = 0; i < arr.length / 3; i++) {
        arr[i * 3 + 1] -= this.rainVel[i] * dt;
        if (arr[i * 3 + 1] < 0) {
          arr[i * 3 + 1] = 7;
          arr[i * 3] = around.x + (Math.random() - 0.5) * 16;
          arr[i * 3 + 2] = around.z - 10 + Math.random() * 12;
        }
      }
      a.needsUpdate = true;
    }
    if (this.snow.visible) {
      const a = this.snow.geometry.getAttribute('position') as THREE.BufferAttribute;
      const arr = a.array as Float32Array;
      const t = performance.now() / 1000;
      for (let i = 0; i < arr.length / 3; i++) {
        arr[i * 3 + 1] -= 0.8 * dt;
        arr[i * 3] += Math.sin(t + this.snowPhase[i]) * 0.3 * dt;
        if (arr[i * 3 + 1] < 0) {
          arr[i * 3 + 1] = 7;
          arr[i * 3] = around.x + (Math.random() - 0.5) * 16;
          arr[i * 3 + 2] = around.z - 10 + Math.random() * 12;
        }
      }
      a.needsUpdate = true;
    }
  }
}
